<!-- eslint-disable vue/no-v-html -->
<template>
  <fieldset class="question">
    <legend
      class="question__title"
      tabindex="0"
      v-html="questionData.title"
    />

    <button
      v-if="hasTooltip"
      v-tag:useractionevent.dom
      class="tooltip__button"
      :data-tag-action="currentStepNumber"
      data-tag-category="skincare finder"
      data-tag-ecommerce="undefined"
      :data-tag-label="questionData.tooltipTriggerLabel"
      data-tag-wrapper
      dom="true"
      event="useractionevent"
      @click="openModal"
    >
      <span
        class="tooltip__copy"
        v-text="questionData.tooltipTriggerLabel"
      />

      <svg
        aria-hidden="true"
        class="tooltip__icon"
        role="img"
      >
        <use xlink:href="#icon-info-outline" />
      </svg>
    </button>

    <div
      v-if="disclaimerCopy"
      class="question__disclaimer"
      :class="{ '-expanded': isDisclaimerExpanded }"
    >
      <p
        aria-live="polite"
        class="question__disclaimer-copy"
        v-html="disclaimerCopy"
      />
      <button
        v-if="showDisclaimerButton"
        class="question__disclaimer-button"
        @click="toggleDisclaimerEllipsis"
        v-text="disclaimerButtonLabel"
      />
    </div>

    <ul
      class="question__selections"
      :class="`question__selections--${questionData.theme}`"
      :style="questionStyles"
    >
      <slot />
    </ul>

    <question-modal
      v-if="hasTooltip"
      :confirm-label="questionData.tooltipConfirmLabel"
      :slides="questionData.slides"
      :tab-control-grid="questionData.tabControlGrid"
      :title="questionData.tooltipTitle"
    />
  </fieldset>
</template>

<script>
  import { eventBus } from '@Foundation/eventbus/eventbus';
  import { mapGetters } from 'vuex';
  import { tag } from '@Foundation/analyticsHandler/directives/tag';
  import { throttle } from '@Foundation/utilities';
  import { modalEventBus, storeNamespace } from '../constants';
  import QuestionModal from './QuestionModal.vue';

  export default {
    name: 'Question',

    directives: { tag },

    components: { QuestionModal },

    props: {
      disclaimerButtonLabels: {
        required: true,
        type: Object,
      },
      questionData: {
        type: Object,
        required: true
      }
    },
    data() {
      return {
        disclaimerMaxCharacters: 245,
        isDisclaimerExpanded: false,
        smallScreen: !window.matchMedia('(min-width: 1025px)').matches,
      };
    },
    computed: {
      ...mapGetters(storeNamespace, [
        'currentStepNumber',
      ]),

      disclaimerButtonLabel() {
        const { less, more } = this.disclaimerButtonLabels;

        return this.isDisclaimerExpanded ? less : more;
      },

      disclaimerCopy() {
        const fullCopy = this.questionData.disclaimerCopy;

        if (!fullCopy) return;

        if (this.isDisclaimerExpanded) {
          return fullCopy;
        }

        if (fullCopy.length > this.disclaimerMaxCharacters) {
          const lastSpace = fullCopy.lastIndexOf(' ', this.disclaimerMaxCharacters);

          if (lastSpace !== -1) {
            return fullCopy.substring(0, lastSpace) + '...';
          }
        }

        return fullCopy;
      },

      hasTooltip() {
        return this.questionData.slides && this.questionData.slides.length > 0;
      },

      questionStyles() {
        const { selections } = this.questionData;
        const hasInlineSelections = selections && selections.length <= 4;
        let styles = '';

        if (hasInlineSelections) {
          styles += '--max-size: 230px;';
          styles += '--selection-ratio: 1;';
        }

        if (hasInlineSelections && !this.smallScreen) {
          styles += `--columns: ${selections.length};`;
        }

        return styles;
      },

      showDisclaimerButton() {
        return this.disclaimerButtonLabel && this.questionData.disclaimerCopy.length > this.disclaimerMaxCharacters;
      },
    },
    mounted() {
      /* istanbul ignore next */
      window.addEventListener('resize', throttle(() => {
        this.smallScreen = !window.matchMedia(`(min-width: 1025px)`).matches;
      }, 100));
    },

    methods: {
      openModal() {
        eventBus.$emit(modalEventBus, true);
      },

      toggleDisclaimerEllipsis() {
        this.isDisclaimerExpanded = !this.isDisclaimerExpanded;
      },
    }
  };
</script>
